import React, { useEffect } from 'react';
import { useParams, useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Constants
import PageUrls from 'Shared/constants/PageUrls.constants';

// Hooks
import { useFeature, useToggle } from 'Shared/utils/hooks';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useUser } from 'Shared/utils/hooks/use-user';
import { useOrganization } from 'Shared/utils/hooks/use-organization';
import { useApp } from 'Shared/utils/hooks/use-app';

// Actions
import * as UsersActions from 'Shared/actions/Users.actions';

// Components
import UserDetails from './UserDetails';
import UserProfileKudos from './ProfileContents/UserProfileKudos';
// import UserProfileActionItems from './ProfileContents/UserProfileActionItems';
import UserProfileGoals from './ProfileContents/UserProfileGoals';
import UserBgImage from './ProfileContents/UserBgImage';
import ItemDoesntExistError from 'Shared/components/ItemDoesntExistError/ItemDoesntExistError';
import UserBaProfileSnapshot from 'Shared/components/UserBaProfileSnapshot/UserBaProfileSnapshot';

// Utils
import { trackEvent } from 'Shared/tracking/tracking';

// Constants
import TrackingConstants from 'Shared/constants/Tracking.constants';

// Styles
import './UserProfileBody.scss';
import { Feature } from 'Shared/types/Organization.type';

const UserProfileBody = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { userSlug } = useParams();
  const { featureFlagShowBaInfoInUserProfile } = useFlags();
  const { isZoom } = useApp();
  const { slug: currentUserSlug } = useCurrentUser();
  const { isActive: isKudosActive } = useFeature(Feature.kudos);

  const [hasError, toggleHasError] = useToggle(false);

  // show current user profile if no slug is specified
  const slugToFetch = userSlug || currentUserSlug;
  const { user, isPiUser } = useUser({ slug: slugToFetch });
  const { isPiDemoAccount } = useOrganization();
  const isPiUserOrPiDemoAccount = isPiUser || isPiDemoAccount;

  useEffect(() => {
    trackEvent('Viewed User Profile', {
      [TrackingConstants.USER_PROFILE.VIEWING_OWN_PROFILE]: currentUserSlug == slugToFetch,
    });
  }, [userSlug]);

  useEffect(() => {
    dispatch(UsersActions.fetchUserBySlug(slugToFetch))
      .then(response => {
        if (!response) toggleHasError();
      })
      .catch(() => {
        toggleHasError();
      });
  }, []);

  const baseClassName = 'UserProfileBody';

  const handleToTeamClick = () => {
    history.push(PageUrls.TEAM_PAGE);
  };

  if (hasError) {
    return (
      <ItemDoesntExistError
        title="We couldn’t find this user."
        buttonLabel="View team"
        description="You either don't have access to view this profile, or the user doesn't exist."
        buttonCallback={handleToTeamClick}
      />
    );
  }

  if (!user) return null;

  return (
    <div>
      <UserBgImage user={user} />
      <div className={`${baseClassName}__content`}>
        <div className={`${baseClassName}__content__aside`}>
          <div className={`${baseClassName}__content__aside__section`}>
            <UserDetails user={user} />
          </div>
          {!isZoom && featureFlagShowBaInfoInUserProfile && isPiUserOrPiDemoAccount && (
            <div className={`${baseClassName}__content__aside__section`}>
              <UserBaProfileSnapshot
                user={user}
                showViewInDrawerButton={true}
                showDownloadReportButton={false}
                showLearnMoreLink={false}
              />
            </div>
          )}
        </div>
        <div className={`${baseClassName}__content__main`}>
          {isKudosActive && (
            <div className={`${baseClassName}__content__main__section`}>
              <UserProfileKudos user={user} />
            </div>
          )}
          <div className={`${baseClassName}__content__main__section`}>
            <UserProfileGoals user={user} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileBody;
